import { groupBy } from 'helpers/groupBy';
import dayjs from 'dayjs';

import {
  useDashboardDataQuery,
  DashboardDataQuery,
} from './DashboardData.generated';
import Bugsnag from '@bugsnag/js';
import { useEffect } from 'react';
import { ConvertFunction } from 'toolbox/src/hooks/useFetchConversionRates';

interface Props {
  organisation_id: number;
  business_unit_id?: number;
  currency_book_id?: number | string;
  convert: ConvertFunction;
  shouldUseModelBalances: boolean;
}

const useDashboardData = ({
  organisation_id = 0,
  business_unit_id,
  currency_book_id,
  shouldUseModelBalances,
  convert,
}: Props) => {
  const [
    { data: unfilteredData, fetching, error },
    refetch,
  ] = useDashboardDataQuery({
    variables: {
      organisation_id,
      business_unit_id,
    },
    // pollInterval: 3_600_000, // 1hour
  });

  // Filter data returned from db if necessary
  const actual_balances =
    unfilteredData?.actual_balances.filter((balance) => {
      if (currency_book_id === 'all') return true;
      if (currency_book_id === undefined) {
        switch (balance.bank_account?.currency?.prefix) {
          case 'GBP':
          case 'EUR':
          case 'USD':
            return true;
          default:
            return false;
        }
      }
      if (balance.bank_account.currency_book.id === currency_book_id)
        return true;
      return false;
    }) ?? [];
  const business_units = unfilteredData?.business_units ?? [];
  const currency_books =
    unfilteredData?.currency_books.filter((currency_book) => {
      if (currency_book_id === 'all') return true;
      if (currency_book_id === undefined) {
        switch (currency_book.currency.prefix) {
          case 'GBP':
          case 'EUR':
          case 'USD':
            return true;
          default:
            return false;
        }
      }
      if (currency_book.id === currency_book_id) return true;
      return false;
    }) ?? [];
  const organisation = unfilteredData?.organisations_by_pk;
  const bank_accounts =
    unfilteredData?.organisations_by_pk?.bank_accounts.filter((account) => {
      if (currency_book_id === 'all') return true;
      if (currency_book_id === undefined) {
        switch (account?.currency?.prefix) {
          case 'GBP':
          case 'EUR':
          case 'USD':
            return true;
          default:
            return false;
        }
      }
      if (account.currency_book_id === currency_book_id) return true;
      return false;
    }) ?? [];

  useEffect(() => {
    if (error) Bugsnag.notify(error);
  }, [error]);

  return {
    refetch,
    ...calculateDashboardData({
      actual_balances,
      business_units,
      currency_books,
      bank_accounts,
      organisation,
      convert,
      shouldUseModelBalances,
      business_unit_id,
      currency_book_id,
    }),
    fetching,
    error,
  };
};

export default useDashboardData;

export const getAggregateBalance = (
  balances: DashboardDataQuery['actual_balances'],
  key: keyof DashboardDataQuery['actual_balances'][0],
) => {
  return balances?.reduce(
    // accumulate the latest balance of each bank_account
    (accumulator, actual_balance) => {
      let resolvedBalance: number = actual_balance[key];
      return accumulator + resolvedBalance;
    },
    0,
  );
};

/**
 * This aggregates the balance history across three currencies
 */
export const getAggregateBalanceHistory = (
  actual_balances: BalanceWithRequiredEquivalents[],
) => {
  return Object.values(groupBy(actual_balances, 'date')).map((balances) => {
    const result = {
      date: balances[0]?.date, // each actual_balance in the groups should have same date
      aggr_balance_gbp: getAggregateBalance(balances, 'balance_gbp'),
      aggr_yield_gbp: balances?.reduce(
        // accumulate the latest yield (bal * interest) of each bank_account
        (sum, balance) =>
          sum +
          (balance?.balance_gbp ?? 0) * ((balance?.interest_rate ?? 0) / 100),
        0,
      ),
      aggr_balance_eur: getAggregateBalance(balances, 'balance_eur'),
      aggr_yield_eur: balances?.reduce(
        // accumulate the latest yield (bal * interest) of each bank_account
        (sum, balance) =>
          sum +
          (balance?.balance_eur ?? 0) * ((balance?.interest_rate ?? 0) / 100),
        0,
      ),
      aggr_balance_usd: getAggregateBalance(balances, 'balance_usd'),
      aggr_yield_usd: balances?.reduce(
        // accumulate the latest yield (bal * interest) of each bank_account
        (sum, balance) =>
          sum +
          (balance?.balance_usd ?? 0) * ((balance?.interest_rate ?? 0) / 100),
        0,
      ),
    };
    return result;
  });
};

export const getCurrencyBooksFromBankAccounts = (params: {
  bank_accounts: DashboardDataQuery['organisations_by_pk']['bank_accounts'];
  filter?: number | string;
  convert: ConvertFunction;
  shouldUseModelBalances: boolean;
}) => {
  const { shouldUseModelBalances, bank_accounts, convert } = params;

  return (
    Object.values(groupBy(bank_accounts, 'currency_book_id'))
      ?.map(
        // iterating over the bank_accounts of a currency book
        (bank_accounts) => {
          const result = bank_accounts.reduce(
            // iterating over bank_accounts in a currency_book
            (acc, cur, i) => {
              const mostRecentBalanceEntry = convertMissingActualBalancesValues(
                shouldUseModelBalances && cur.model_balances?.[0]
                  ? {
                      balance_gbp: cur.model_balances?.[0]?.balance_gbp ?? 0,
                      balance: cur.model_balances?.[0]?.balance ?? 0,
                      interest_rate: cur?.opening_interest_rate ?? 0,
                    }
                  : cur.actual_balances[0] ?? {
                      balance: 0,
                      balance_gbp: 0,
                      balance_eur: 0,
                      balance_usd: 0,
                    },
                convert,
              );

              const balanceEntryToCompareTo = convertMissingActualBalancesValues(
                cur.actual_balances?.[1] ?? {
                  balance: 0,
                  balance_gbp: 0,
                  balance_eur: 0,
                  balance_usd: 0,
                },
                convert,
              );

              const interestRate = cur.model_balances?.[0]
                ? cur?.opening_interest_rate ?? 0
                : cur?.actual_balances?.[0]?.interest_rate ??
                  cur?.opening_interest_rate ??
                  0;

              const result = {
                id: bank_accounts[0]?.currency_book_id,
                business_unit_name:
                  bank_accounts[0]?.currency_book?.business_unit.name,
                name: bank_accounts[0]?.currency_book?.name,
                accounts: i + 1,
                aggr_balance:
                  acc.aggr_balance + (mostRecentBalanceEntry?.balance ?? 0),
                aggr_balance_gbp:
                  acc.aggr_balance_gbp +
                  (mostRecentBalanceEntry?.balance_gbp ?? 0),
                aggr_balance_eur:
                  acc.aggr_balance_eur +
                  (mostRecentBalanceEntry?.balance_eur ?? 0),
                aggr_balance_usd:
                  acc.aggr_balance_usd +
                  (mostRecentBalanceEntry?.balance_usd ?? 0),
                aggr_yield:
                  acc.aggr_yield +
                  (mostRecentBalanceEntry?.balance ?? 0) * (interestRate / 100),
                aggr_yield_gbp:
                  acc.aggr_yield_gbp +
                  (mostRecentBalanceEntry?.balance_gbp ?? 0) *
                    (interestRate / 100),
                aggr_yield_eur:
                  acc.aggr_yield_eur +
                  (mostRecentBalanceEntry?.balance_eur ?? 0) *
                    (interestRate / 100),
                aggr_yield_usd:
                  acc.aggr_yield_usd +
                  (mostRecentBalanceEntry?.balance_usd ?? 0) *
                    (interestRate / 100),
                aggr_yield_change_gbp:
                  acc.aggr_yield_change_gbp +
                  (mostRecentBalanceEntry?.balance_gbp ?? 0) *
                    (interestRate / 100) -
                  (balanceEntryToCompareTo?.balance_gbp *
                    (interestRate / 100) ||
                    (mostRecentBalanceEntry?.balance_gbp ?? 0) *
                      (interestRate / 100)),
                aggr_yield_change_eur:
                  acc.aggr_yield_change_eur +
                  (mostRecentBalanceEntry?.balance_eur ?? 0) *
                    (interestRate / 100) -
                  (balanceEntryToCompareTo?.balance_eur *
                    (interestRate / 100) ||
                    (mostRecentBalanceEntry?.balance_eur ?? 0) *
                      (interestRate / 100)),
                aggr_yield_change_usd:
                  acc.aggr_yield_change_usd +
                  (mostRecentBalanceEntry?.balance_usd ?? 0) *
                    (interestRate / 100) -
                  (balanceEntryToCompareTo?.balance_usd *
                    (interestRate / 100) ||
                    (mostRecentBalanceEntry?.balance_usd ?? 0) *
                      (interestRate / 100)),
                // Compare this to the previous balance, where 0
                // is the most recent and 1 being the second most recent
                aggr_balance_change:
                  acc.aggr_balance_change +
                  ((mostRecentBalanceEntry?.balance ?? 0) -
                    (balanceEntryToCompareTo?.balance ?? 0)),
                aggr_balance_change_gbp:
                  acc.aggr_balance_change_gbp +
                  ((mostRecentBalanceEntry?.balance_gbp ?? 0) -
                    (balanceEntryToCompareTo?.balance_gbp ?? 0)),
                aggr_balance_change_eur:
                  acc.aggr_balance_change_eur +
                  ((mostRecentBalanceEntry?.balance_eur || 0) -
                    (balanceEntryToCompareTo?.balance_eur || 0)),
                aggr_balance_change_usd:
                  acc.aggr_balance_change_usd +
                  ((mostRecentBalanceEntry?.balance_usd || 0) -
                    (balanceEntryToCompareTo?.balance_usd || 0)),
                currency_prefix: bank_accounts[0]?.currency?.prefix,
              };

              return result;
            },
            {
              id: 0,
              name: '',
              business_unit_name: '',
              accounts: 0,
              aggr_balance: 0,
              aggr_balance_gbp: 0,
              aggr_balance_eur: 0,
              aggr_balance_usd: 0,
              aggr_yield: 0,
              aggr_yield_gbp: 0,
              aggr_yield_eur: 0,
              aggr_yield_usd: 0,
              aggr_yield_change_gbp: 0,
              aggr_yield_change_eur: 0,
              aggr_yield_change_usd: 0,
              aggr_balance_change: 0,
              aggr_balance_change_gbp: 0,
              aggr_balance_change_eur: 0,
              aggr_balance_change_usd: 0,
              currency_prefix: '',
            },
          );
          return result;
        },
      )
      .sort((a, b) => (a.aggr_balance > b.aggr_balance ? -1 : 1)) || []
  );
};

// type CurrencyBook = ReturnType<typeof getCurrencyBooksFromBankAccounts>[0];
type AggregateBalanceHistory = ReturnType<typeof getAggregateBalanceHistory>[0];

export const getAggregateData = (
  currency_books: any[],
  aggr_balance_history: AggregateBalanceHistory[],
  yieldKey: 'aggr_yield_gbp' | 'aggr_yield_usd' | 'aggr_yield_eur',
  yieldChangeKey:
    | 'aggr_yield_change_gbp'
    | 'aggr_yield_change_usd'
    | 'aggr_yield_change_eur',
  balanceKey: 'aggr_balance_gbp' | 'aggr_balance_usd' | 'aggr_balance_eur',
  balanceChangeKey:
    | 'aggr_balance_change_gbp'
    | 'aggr_balance_change_usd'
    | 'aggr_balance_change_eur',
  shouldUseModelBalances: boolean,
) => {
  const aggr_balance_latest = currency_books.reduce(
    (acc, cur) => acc + (cur?.[balanceKey] ?? 0),
    0,
  );

  const aggr_balance_previous = shouldUseModelBalances
    ? aggr_balance_history?.[0]?.[balanceKey] ?? 0
    : aggr_balance_history?.[1]?.[balanceKey] ?? 0;

  const aggr_yield_latest = currency_books.reduce(
    (acc, cur) => acc + (cur?.[yieldKey] ?? 0),
    0,
  );

  const aggr_yield_previous = shouldUseModelBalances
    ? aggr_balance_history?.[0]?.[yieldKey] ?? 0
    : aggr_balance_history?.[1]?.[yieldKey] ?? 0;

  // const aggr_balance_change = currency_books.reduce(
  //   (acc, cur) => acc + (cur?.[balanceChangeKey] ?? 0),
  //   0,
  // );
  const aggr_balance_change = aggr_balance_latest - aggr_balance_previous;

  const aggr_balance_change_pct = aggr_balance_change
    ? (aggr_balance_change / aggr_balance_previous) * 100
    : 0;

  const aggr_yield_change = aggr_yield_latest - aggr_yield_previous;
  // const aggr_yield_change = currency_books.reduce(
  //   (acc, cur) => acc + (cur?.[yieldChangeKey] ?? 0),
  //   0,
  // );

  const rate = (aggr_yield_latest / aggr_balance_latest) * 100;
  const aggr_yield_change_pct = aggr_yield_change
    ? (aggr_yield_change / aggr_yield_latest) * rate
    : 0;

  return {
    aggr_balance_latest,
    aggr_balance_previous,
    aggr_yield_latest,
    aggr_yield_previous,
    aggr_balance_change,
    aggr_balance_change_pct,
    aggr_yield_change,
    aggr_yield_change_pct,
  };
};

export const getLiquidityBrackets = ({
  bank_accounts,
  aggr_balance_gbp,
  liquidity_limits,
  convert,
  shouldUseModelBalances,
}: {
  bank_accounts: DashboardDataQuery['organisations_by_pk']['bank_accounts'];
  aggr_balance_gbp: number;
  liquidity_limits: DashboardDataQuery['organisations_by_pk']['organisation_liquidity_limits'];
  convert: ConvertFunction;
  shouldUseModelBalances: boolean;
}) => {
  const LIQUIDITY_BRACKETS = [
    { gte: 0, lt: 1 }, // CALL
    { gte: 1, lt: 8 }, // 1 to 7 days
    { gte: 8, lt: 36 }, // 8 to 35 days
    { gte: 36, lt: 96 }, // 36 to 95 days
    { gte: 96 },
  ];

  return LIQUIDITY_BRACKETS.map((bracket) => {
    return {
      name: bracket.lt
        ? bracket.gte
          ? `${bracket.gte} to ${bracket.lt - 1}D`
          : `CALL`
        : `${bracket.gte}D${bracket.gte === 96 && '+'}`,

      ...bank_accounts
        /** Filter the bank accounts by which dates match the current iteration bracket */
        .filter((bank_account) => {
          if (bracket.gte === 96 && bank_account.notice_days >= 96)
            console.log(bracket, bank_account);

          if (
            bank_account?.product_type === 'NOTICE' &&
            (bank_account?.notice_days >= bracket?.gte ||
              bracket?.gte === undefined) &&
            (bank_account?.notice_days < bracket?.lt ||
              bracket?.lt === undefined)
          )
            return true;

          if (bank_account?.product_type === 'FIXED') {
            const maturity_date = dayjs(bank_account?.fixed_maturity_date);
            const today = dayjs(new Date());
            const diff = maturity_date.diff(today, 'day');

            if (diff >= bracket?.gte && (!bracket?.lt || diff < bracket?.lt))
              return true;
          }

          if (
            bank_account?.product_type === 'CALL' &&
            bracket?.gte === 0 &&
            bracket?.lt === 1
          )
            return true;

          // else, the account is not relevant to this bracket...
          return false;
        })
        ?.reduce(
          /* for each bank_account in the bracket ... */
          (acc, cur) => {
            const resolvedBalance = convertMissingActualBalancesValues(
              shouldUseModelBalances && cur.model_balances[0]
                ? {
                    balance_gbp: cur.model_balances[0].balance_gbp ?? 0,
                    balance: cur.model_balances[0].balance ?? 0,
                    interest_rate: cur?.opening_interest_rate ?? 0,
                  }
                : cur.actual_balances[0] ?? {
                    balance: 0,
                    balance_gbp: 0,
                    balance_eur: 0,
                    balance_usd: 0,
                  },
              convert,
            );

            return {
              accounts: acc?.accounts + 1,
              lt: bracket.lt,
              gte: bracket.gte,
              aggr_balance_gbp:
                acc?.aggr_balance_gbp + resolvedBalance?.balance_gbp,
              aggr_balance_eur:
                acc?.aggr_balance_eur + resolvedBalance?.balance_eur,
              aggr_balance_usd:
                acc?.aggr_balance_usd + resolvedBalance?.balance_usd,
              aggr_balance_pct:
                (resolvedBalance?.balance_gbp / aggr_balance_gbp) * 100 +
                acc?.aggr_balance_pct,
            };
          },
          {
            accounts: 0,
            lt: bracket.lt,
            gte: bracket.gte,
            aggr_balance_gbp: 0,
            aggr_balance_usd: 0,
            aggr_balance_eur: 0,
            aggr_balance_pct: 0,
          },
        ),
    };
  }).map((bracket) => {
    const liquidity_limit = liquidity_limits?.find(
      (limit) =>
        (limit?.notice_days_lt === bracket?.lt &&
          limit?.notice_days_gte === bracket?.gte) ||
        (limit?.notice_days_lt === bracket?.lt && !bracket?.gte) ||
        (limit?.notice_days_gte === bracket?.gte && !bracket?.lt),
    );

    const shouldDisplayAsRed =
      bracket?.aggr_balance_pct > liquidity_limit?.upper_limit_pct ||
      bracket?.aggr_balance_pct < liquidity_limit?.lower_limit_pct;

    return {
      ...bracket,
      shouldDisplayAsRed,
      upper_limit_pct: liquidity_limit?.upper_limit_pct,
      lower_limit_pct: liquidity_limit?.lower_limit_pct,
    };
  });
};

export const getAggregatedBanksData = (
  bank_accounts: DashboardDataQuery['organisations_by_pk']['bank_accounts'],
  bank_exposure_limits: DashboardDataQuery['organisations_by_pk']['organisation_bank_exposure_limits'],
  aggr_balance_gbp: number,
  convert: ConvertFunction,
  shouldUseModelBalances: boolean,
  currencyFilter?: 'all' | string,
) => {
  const aggregatedBankData =
    Object.values(groupBy(bank_accounts, 'bank_id')).map(
      // iterating over banks
      (bank_accounts) =>
        bank_accounts.reduce(
          // iterating over bank_accounts in a bank
          (acc, cur) => {
            const resolvedBalance = convertMissingActualBalancesValues(
              shouldUseModelBalances && cur.model_balances[0]
                ? {
                    balance_gbp: cur.model_balances[0].balance_gbp ?? 0,
                    // balance_eur: cur.model_balances[0].balance_eur ?? 0,
                    // balance_usd: cur.model_balances[0].balance_usd ?? 0,
                    balance: cur.model_balances[0].balance ?? 0,
                    interest_rate: cur?.opening_interest_rate ?? 0,
                  }
                : cur.actual_balances[0] ?? {
                    balance: 0,
                    balance_gbp: 0,
                    balance_eur: 0,
                    balance_usd: 0,
                  },
              convert,
            );

            return {
              name: bank_accounts[0]?.bank?.name,
              accounts: bank_accounts?.length,
              aggr_balance_gbp: Number(
                acc.aggr_balance_gbp + (resolvedBalance?.balance_gbp ?? 0),
              ),
              aggr_balance_usd: Number(
                acc.aggr_balance_usd + (resolvedBalance?.balance_usd ?? 0),
              ),
              aggr_balance_eur: Number(
                acc.aggr_balance_eur + (resolvedBalance?.balance_eur ?? 0),
              ),
              logo: bank_accounts[0]?.bank?.image?.public_url,
              limit_pct: Number(
                bank_exposure_limits?.find(
                  (bank_exposure_limit) =>
                    bank_exposure_limit?.bank_id === bank_accounts[0]?.bank_id,
                )?.exposure_limit ?? 100,
              ),
              pct_of_total_balance:
                acc.pct_of_total_balance +
                ((resolvedBalance?.balance_gbp ?? 0) / aggr_balance_gbp) * 100,
            };
          },
          {
            name: '',
            accounts: 0,
            aggr_balance_gbp: 0,
            aggr_balance_eur: 0,
            aggr_balance_usd: 0,
            logo: '',
            limit_pct: 0,
            pct_of_total_balance: 0,
          },
        ),
    ) || [];
  return aggregatedBankData;
};

export type BalanceWithRequiredEquivalents<
  T = DashboardDataQuery['actual_balances'][0]
> = T & {
  balance_eur: number;
  balance_usd: number;
};

/**
 * This function ensures that balance_eur and balance_usd
 * have been converted, and are not just nullable,
 * like they are in the db
 */
export const convertArrayOfMissingActualBalancesValues = (
  balances: DashboardDataQuery['actual_balances'],
  convert: ConvertFunction,
): BalanceWithRequiredEquivalents[] => {
  return balances.map((balance) =>
    convertMissingActualBalancesValues(balance, convert),
  );
};

/**
 * Fills out the other currencies if balance_gbp exists
 */
export const convertMissingActualBalancesValues = <
  T extends Pick<
    DashboardDataQuery['organisations_by_pk']['bank_accounts'][0]['actual_balances'][0],
    'balance_gbp' | 'balance_eur' | 'balance_usd'
  >
>(
  balance: T,
  convert: ConvertFunction,
): BalanceWithRequiredEquivalents<T> => {
  return {
    ...balance,
    balance_eur:
      balance?.balance_eur ||
      convert({
        base: 'GBP',
        amount: balance?.balance_gbp,
        target: 'EUR',
      }),
    balance_usd:
      balance?.balance_usd ||
      convert({
        base: 'GBP',
        amount: balance?.balance_gbp,
        target: 'USD',
      }),
  };
};

export const getBankExposureLimitsBasedOnFilters = ({
  currency_book_id,
  currency_books,
  business_unit_id,
  business_units,
  organisation,
}: {
  currency_book_id: number | string | 'all' | undefined;
  business_unit_id: number | undefined;
  currency_books: DashboardDataQuery['currency_books'];
  business_units: DashboardDataQuery['business_units'];
  organisation: DashboardDataQuery['organisations_by_pk'];
}) => {
  let bank_exposure_limits = [];
  switch (true) {
    case !!(
      currency_book_id &&
      currency_book_id !== 'all' &&
      currency_books?.[0]?.currency_book_bank_exposure_limits
    ):
      bank_exposure_limits =
        currency_books?.[0]?.currency_book_bank_exposure_limits;
      break;
    case !!(
      business_unit_id &&
      business_units?.[0]?.business_unit_bank_exposure_limits
    ):
      bank_exposure_limits =
        business_units?.[0]?.business_unit_bank_exposure_limits;
      break;
    case !!organisation?.organisation_bank_exposure_limits:
      bank_exposure_limits = organisation?.organisation_bank_exposure_limits;
      break;
  }

  return bank_exposure_limits;
};

export const calculateDashboardData = ({
  // data,
  actual_balances,
  currency_books,
  business_units,
  bank_accounts,
  organisation,
  convert,
  shouldUseModelBalances,
  business_unit_id,
  currency_book_id,
}: {
  // data: DashboardDataQuery | undefined;
  actual_balances: DashboardDataQuery['actual_balances'];
  currency_books: DashboardDataQuery['currency_books'];
  business_units: DashboardDataQuery['business_units'];
  bank_accounts: DashboardDataQuery['organisations_by_pk']['bank_accounts'];
  organisation: DashboardDataQuery['organisations_by_pk'];
  convert: ConvertFunction;
  shouldUseModelBalances: boolean;
  business_unit_id?: number;
  currency_book_id?: number | string | 'all' | undefined;
}) => {
  let liquidity_limits = [];
  switch (true) {
    case !!(
      currency_book_id && currency_books?.[0]?.currency_book_liquidity_limits
    ):
      liquidity_limits = currency_books?.[0]?.currency_book_liquidity_limits;
      break;
    case !!(
      business_unit_id && business_units?.[0]?.business_unit_liquidity_limits
    ):
      liquidity_limits = business_units?.[0]?.business_unit_liquidity_limits;
      break;
    case !!organisation?.organisation_liquidity_limits:
      liquidity_limits = organisation?.organisation_liquidity_limits ?? [];
      break;
  }

  const actual_balances_converted = convertArrayOfMissingActualBalancesValues(
    actual_balances || [],
    convert,
  );

  const aggr_balance_history = getAggregateBalanceHistory(
    actual_balances_converted,
  );

  // data for currency_book table
  const currency_books_converted = getCurrencyBooksFromBankAccounts({
    bank_accounts,
    filter: currency_book_id,
    convert,
    shouldUseModelBalances,
  });

  const gbpAggregates = getAggregateData(
    currency_books_converted,
    aggr_balance_history,
    'aggr_yield_gbp',
    'aggr_yield_change_gbp',
    'aggr_balance_gbp',
    'aggr_balance_change_gbp',
    shouldUseModelBalances,
  );

  const eurAggregates = getAggregateData(
    currency_books_converted,
    aggr_balance_history,
    'aggr_yield_eur',
    'aggr_yield_change_eur',
    'aggr_balance_eur',
    'aggr_balance_change_eur',
    shouldUseModelBalances,
  );

  const usdAggregates = getAggregateData(
    currency_books_converted,
    aggr_balance_history,
    'aggr_yield_usd',
    'aggr_yield_change_usd',
    'aggr_balance_usd',
    'aggr_balance_change_usd',
    shouldUseModelBalances,
  );

  const bank_exposure_limits = getBankExposureLimitsBasedOnFilters({
    currency_books,
    business_units,
    organisation,
    business_unit_id,
    currency_book_id,
  });

  const banks = getAggregatedBanksData(
    bank_accounts,
    bank_exposure_limits,
    gbpAggregates.aggr_balance_latest,
    convert,
    shouldUseModelBalances,
  );

  const liquidity_brackets = getLiquidityBrackets({
    aggr_balance_gbp: gbpAggregates?.aggr_balance_latest ?? 0,
    bank_accounts,
    liquidity_limits,
    convert,
    shouldUseModelBalances,
  });

  return {
    bank_accounts,
    gbpLatest: {
      aggr_balance: gbpAggregates?.aggr_balance_latest ?? 0,
      aggr_balance_change: gbpAggregates?.aggr_balance_change ?? 0,
      aggr_balance_change_pct: gbpAggregates?.aggr_balance_change_pct ?? 0,
      aggr_yield: gbpAggregates?.aggr_yield_latest ?? 0,
      aggr_yield_change: gbpAggregates?.aggr_yield_change ?? 0,
      aggr_yield_change_pct: gbpAggregates?.aggr_yield_change_pct ?? 0,
      date: aggr_balance_history[0]?.date || null,
    },
    gbpPrevious: {
      aggr_balance: gbpAggregates?.aggr_balance_previous ?? 0,
      aggr_balance_change: gbpAggregates?.aggr_balance_change ?? 0,
      aggr_balance_change_pct: gbpAggregates?.aggr_balance_change_pct ?? 0,
      aggr_yield: gbpAggregates?.aggr_yield_previous ?? 0,
      aggr_yield_change: gbpAggregates?.aggr_yield_change ?? 0,
      aggr_yield_change_pct: gbpAggregates?.aggr_yield_change_pct ?? 0,
      date: aggr_balance_history[1]?.date || null,
    },
    eurLatest: {
      aggr_balance: eurAggregates?.aggr_balance_latest ?? 0,
      aggr_balance_change: eurAggregates?.aggr_balance_change ?? 0,
      aggr_balance_change_pct: eurAggregates?.aggr_balance_change_pct ?? 0,
      aggr_yield: eurAggregates?.aggr_yield_latest ?? 0,
      aggr_yield_change: eurAggregates?.aggr_yield_change ?? 0,
      aggr_yield_change_pct: eurAggregates?.aggr_yield_change_pct ?? 0,
      date: aggr_balance_history[0]?.date || null,
    },
    eurPrevious: {
      aggr_balance: eurAggregates?.aggr_balance_previous ?? 0,
      aggr_balance_change: eurAggregates?.aggr_balance_change ?? 0,
      aggr_balance_change_pct: eurAggregates?.aggr_balance_change_pct ?? 0,
      aggr_yield: eurAggregates?.aggr_yield_previous ?? 0,
      aggr_yield_change: eurAggregates?.aggr_yield_change ?? 0,
      aggr_yield_change_pct: eurAggregates?.aggr_yield_change_pct ?? 0,
      date: aggr_balance_history[1]?.date || null,
    },
    usdLatest: {
      aggr_balance: usdAggregates?.aggr_balance_latest ?? 0,
      aggr_balance_change: usdAggregates?.aggr_balance_change ?? 0,
      aggr_balance_change_pct: usdAggregates?.aggr_balance_change_pct ?? 0,
      aggr_yield: usdAggregates?.aggr_yield_latest ?? 0,
      aggr_yield_change: usdAggregates?.aggr_yield_change ?? 0,
      aggr_yield_change_pct: usdAggregates?.aggr_yield_change_pct ?? 0,
      date: aggr_balance_history[0]?.date ?? null,
    },
    usdPrevious: {
      aggr_balance: usdAggregates?.aggr_balance_previous ?? 0,
      aggr_balance_change: usdAggregates?.aggr_balance_change ?? 0,
      aggr_balance_change_pct: usdAggregates?.aggr_balance_change_pct ?? 0,
      aggr_yield: usdAggregates?.aggr_yield_previous ?? 0,
      aggr_yield_change: usdAggregates?.aggr_yield_change ?? 0,
      aggr_yield_change_pct: usdAggregates?.aggr_yield_change_pct ?? 0,
      date: aggr_balance_history[1]?.date ?? null,
    },
    currency_books: currency_books_converted,
    liquidity_brackets,
    banks,
    aggr_balance_history,
    loading: false,
  };
};
